<form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col max-h-[75vh]">
  <!-- Scrollable Form Body -->
  <div class="flex-1 overflow-y-auto pr-2 space-y-6 min-h-0">

    <!-- Permission Warning (if read-only) -->
    @if (!canEditBilling()) {
      <div class="p-3.5 bg-surface border border-warning rounded-xl flex items-center gap-3 text-xs text-warning">
        <lucide-icon name="shield-alert" class="w-4 h-4 shrink-0"></lucide-icon>
        <app-text size="xs" color="warning">
          Read-only mode: Only Root, Admin, or Accounting team members are authorized to adjust billing and pricing amounts.
        </app-text>
      </div>
    }

    <!-- SECTION 1: Base Freight Charges -->
    <div class="p-4 bg-surface rounded-xl border border-border space-y-4">
      <div class="flex items-center gap-2.5 pb-2 border-b border-border">
        <div class="p-1.5 rounded-lg bg-surface-high text-primary">
          <lucide-icon name="dollar-sign" class="w-4 h-4"></lucide-icon>
        </div>
        <div>
          <app-heading level="h4" class="text-sm font-semibold text-foreground">Base Freight Charges</app-heading>
          <app-text size="xs" color="muted">Agreed primary linehaul rate and contract fuel surcharge</app-text>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <app-form-field label="Base Linehaul Rate" [required]="!isTonu" hint="Agreed freight rate">
          <app-number-input
            formControlName="baseRate"
            currency="USD"
            [showGrouping]="true"
            placeholder="0.00"
            [class]="isTonu ? 'opacity-50' : ''"
          ></app-number-input>
        </app-form-field>

        <app-form-field label="Fuel Surcharge" hint="Separate fuel line item">
          <app-number-input
            formControlName="fuelSurcharge"
            currency="USD"
            [showGrouping]="true"
            placeholder="0.00"
          ></app-number-input>
        </app-form-field>
      </div>
    </div>

    <!-- SECTION 2: Detention & Facility Dwell Time -->
    <div class="p-4 bg-surface rounded-xl border border-border space-y-4">
      <div class="flex items-center justify-between pb-2 border-b border-border">
        <div class="flex items-center gap-2.5">
          <div class="p-1.5 rounded-lg bg-surface-high text-primary">
            <lucide-icon name="timer" class="w-4 h-4"></lucide-icon>
          </div>
          <div>
            <app-heading level="h4" class="text-sm font-semibold text-foreground">Detention Billing</app-heading>
            <app-text size="xs" color="muted">Industry standard 2 hours free time per facility stop</app-text>
          </div>
        </div>
        <app-badge variant="secondary" badgeStyle="outline" size="sm">Detention</app-badge>
      </div>

      <!-- Dwell Time Auto-Detection Banner -->
      @if (detectedDetention(); as detention) {
        <div class="p-3.5 bg-surface-high border border-warning rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-start gap-2.5">
            <lucide-icon name="clock-alert" class="w-5 h-5 text-warning shrink-0 mt-0.5"></lucide-icon>
            <div class="text-xs space-y-0.5">
              <div class="font-medium text-foreground">
                Excess Facility Dwell Time Detected
              </div>
              <div class="text-muted">
                Driver spent &gt; 2 hrs at facility. Detected <strong>{{ detention.totalHours }} hrs</strong> excess across stops.
                Suggested: <strong>{{ detention.suggestedTotal | currency }}</strong> ({{ detention.totalHours }}h @ ${{ detention.suggestedRate }}/h).
              </div>
            </div>
          </div>
          @if (canEditBilling()) {
            <app-button
              size="sm"
              variant="outline"
              icon="Plus"
              (click)="applyDetectedDetention()"
              class="shrink-0 self-end sm:self-center"
            >
              Apply to Pricing
            </app-button>
          }
        </div>
      }

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <app-form-field label="Detention Hours" hint="Billable hours beyond 2h">
          <app-number-input
            formControlName="detentionHours"
            mode="decimal"
            placeholder="0.0"
            (change)="onDetentionHoursOrRateChange()"
          ></app-number-input>
        </app-form-field>

        <app-form-field label="Hourly Rate" hint="Standard $50.00/hr">
          <app-number-input
            formControlName="detentionRatePerHour"
            currency="USD"
            placeholder="50.00"
            (change)="onDetentionHoursOrRateChange()"
          ></app-number-input>
        </app-form-field>

        <app-form-field label="Total Detention" hint="Auto-calculated (Hrs × Rate)">
          <app-number-input
            formControlName="detention"
            currency="USD"
            [showGrouping]="true"
            placeholder="0.00"
          ></app-number-input>
        </app-form-field>
      </div>
    </div>

    <!-- SECTION 3: Accessorials & Reimbursements -->
    <div class="p-4 bg-surface rounded-xl border border-border space-y-4">
      <div class="flex items-center justify-between pb-2 border-b border-border">
        <div class="flex items-center gap-2.5">
          <div class="p-1.5 rounded-lg bg-surface-high text-primary">
            <lucide-icon name="receipt" class="w-4 h-4"></lucide-icon>
          </div>
          <div>
            <app-heading level="h4" class="text-sm font-semibold text-foreground">Accessorials & Reimbursements</app-heading>
            <app-text size="xs" color="muted">Driver layovers and reimbursable warehouse lumper fees</app-text>
          </div>
        </div>
        @if (detectedLumperReceipts().length > 0) {
          <app-badge variant="info" badgeStyle="soft" size="sm">
            {{ detectedLumperReceipts().length }} Lumper Receipt{{ detectedLumperReceipts().length > 1 ? 's' : '' }} Found
          </app-badge>
        }
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <app-form-field label="Layover Fee" hint="Daily layover compensation">
          <app-number-input
            formControlName="layover"
            currency="USD"
            [showGrouping]="true"
            placeholder="0.00"
          ></app-number-input>
        </app-form-field>

        <app-form-field label="Lumper Fee" hint="Reimbursable unloading cost">
          <app-number-input
            formControlName="lumper"
            currency="USD"
            [showGrouping]="true"
            placeholder="0.00"
          ></app-number-input>
        </app-form-field>
      </div>

      <!-- Detected Lumper Receipts from Stops / Load Documents -->
      @if (detectedLumperReceipts().length > 0) {
        <div class="p-3.5 bg-background rounded-xl border border-border space-y-3">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <lucide-icon name="paperclip" class="w-4 h-4 text-primary"></lucide-icon>
              <app-text size="xs" weight="medium" class="text-foreground">Attached Lumper Receipts from Stops</app-text>
            </div>
            @if (detectedLumperReceipts().length > 1 && canEditBilling()) {
              <app-button
                size="xs"
                variant="outline"
                icon="CheckCircle2"
                (click)="attachAllLumperReceipts()"
              >
                Attach All Receipts
              </app-button>
            }
          </div>

          <div class="space-y-2">
            @for (receipt of detectedLumperReceipts(); track receipt.id) {
              <div class="p-2.5 rounded-lg border border-border bg-surface flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs">
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="p-1 rounded bg-surface-high text-muted shrink-0">
                    <lucide-icon name="file-text" class="w-3.5 h-3.5"></lucide-icon>
                  </div>
                  <div class="min-w-0">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <span class="font-medium text-foreground truncate">{{ receipt.label }}</span>
                      @if (receipt.facility) {
                        <span class="text-muted truncate">({{ receipt.facility }})</span>
                      }
                      @if (receipt.fee) {
                        <span class="font-semibold text-primary">{{ receipt.fee | currency }}</span>
                      }
                    </div>
                    @if (receipt.paidBy || receipt.paymentMethod) {
                      <div class="text-[11px] text-muted flex items-center gap-1.5 mt-0.5">
                        @if (receipt.paidBy) {
                          <span>Paid by: {{ receipt.paidBy | titlecase }}</span>
                        }
                        @if (receipt.paymentMethod) {
                          <span>&bull; Method: {{ receipt.paymentMethod | uppercase }}</span>
                        }
                      </div>
                    }
                  </div>
                </div>

                <div class="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                  <app-button
                    size="xs"
                    variant="ghost"
                    icon="Eye"
                    (click)="viewReceipt(receipt.url, receipt.label)"
                  >
                    View
                  </app-button>
                  <app-button
                    size="xs"
                    variant="ghost"
                    [iconOnly]="true"
                    icon="ExternalLink"
                    title="Open in new tab"
                    (click)="openReceiptExternal(receipt.url)"
                  ></app-button>
                  @if (form.get('lumperReceiptUrl')?.value?.includes(receipt.url)) {
                    <app-badge variant="success" badgeStyle="soft" size="xs">
                      Attached
                    </app-badge>
                  } @else if (canEditBilling()) {
                    <app-button
                      size="xs"
                      variant="secondary"
                      (click)="attachLumperReceipt(receipt)"
                    >
                      Attach
                    </app-button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }

      <app-form-field
        label="Lumper Receipt URL or Reference"
        [hint]="detectedLumperReceipts().length > 0 ? 'Auto-populated from detected receipts above, or enter custom URL/reference' : 'Attach receipt proof or external link for broker invoice compliance'"
      >
        <app-input
          formControlName="lumperReceiptUrl"
          placeholder="https://... or receipt reference"
        ></app-input>
      </app-form-field>

      <div class="pt-1">
        <app-checkbox
          formControlName="lumperReceiptWaived"
          size="sm"
          label="Lumper receipt does not exist / paid directly by broker (do not require receipt for invoicing)"
        />
      </div>
    </div>

    <!-- SECTION 4: Adjustments & Deductions -->
    <div class="p-4 bg-surface rounded-xl border border-border space-y-4">
      <div class="flex items-center gap-2.5 pb-2 border-b border-border">
        <div class="p-1.5 rounded-lg bg-surface-high text-primary">
          <lucide-icon name="sliders-horizontal" class="w-4 h-4"></lucide-icon>
        </div>
        <div>
          <app-heading level="h4" class="text-sm font-semibold text-foreground">Adjustments & Deductions</app-heading>
          <app-text size="xs" color="muted">Miscellaneous charges (scales, tolls) or broker deduction claims</app-text>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Other Billable Charges -->
        <div class="p-3.5 rounded-lg border border-border bg-background space-y-3">
          <app-form-field label="Other Charges" hint="Scale tickets, tolls, escort fees">
            <app-number-input
              formControlName="otherCharges"
              currency="USD"
              [showGrouping]="true"
              placeholder="0.00"
            ></app-number-input>
          </app-form-field>
          <app-form-field label="Other Charges Description">
            <app-input
              formControlName="otherChargesDescription"
              placeholder="e.g. Escort fee, bridge toll"
            ></app-input>
          </app-form-field>
        </div>

        <!-- Broker Deductions / Claims -->
        <div class="p-3.5 rounded-lg border border-border bg-background space-y-3">
          <app-form-field label="Deductions" hint="Broker penalties, late fees, claims">
            <app-number-input
              formControlName="deductions"
              currency="USD"
              [showGrouping]="true"
              placeholder="0.00"
              [isNegative]="true"
              class="text-danger"
            ></app-number-input>
          </app-form-field>
          <app-form-field label="Deductions Description">
            <app-input
              formControlName="deductionsDescription"
              placeholder="e.g. Late delivery penalty"
            ></app-input>
          </app-form-field>
        </div>
      </div>
    </div>

    <!-- Live Total Summary Card -->
    <div class="p-4 bg-surface-high rounded-xl border border-border flex justify-between items-center">
      <div class="flex flex-col">
        <span class="text-xs text-muted font-medium uppercase tracking-wider">Total Invoice Amount</span>
        <span class="text-xs text-muted">Sum of linehaul, detention, layover, lumper, other charges minus deductions</span>
      </div>
      <div class="text-2xl font-bold" [ngClass]="total < 0 ? 'text-danger' : 'text-primary'">
        {{ total | currency }}
      </div>
    </div>

  </div>

  <!-- FIXED Action Footer (Always pinned at bottom, never scrolls away) -->
  <div class="flex-shrink-0 pt-4 mt-4 border-t border-border flex items-center justify-between bg-background sticky bottom-0 z-10">
    <div class="flex items-center gap-2">
      <app-text variant="muted" size="xs" class="uppercase tracking-wider">Estimated Total:</app-text>
      <span class="text-lg font-bold" [ngClass]="total < 0 ? 'text-danger' : 'text-primary'">
        {{ total | currency }}
      </span>
    </div>

    <div class="flex items-center gap-3">
      <app-button
        type="button"
        variant="ghost"
        (click)="close()"
        [disabled]="isSaving()"
      >
        Cancel
      </app-button>
      <app-button
        type="submit"
        variant="primary"
        [loading]="isSaving()"
        [disabled]="form.invalid || !canEditBilling() || isSaving()"
        icon="Check"
      >
        Save Pricing
      </app-button>
    </div>
  </div>
</form>
